import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Divider, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import SaveStatusIcon from './SaveStatusIcon';

// `required` fields must be present for the prior authorization to be inserted —
// if any is missing, all distribution rows for the entry are skipped on upload.
const PRIOR_AUTH_HEADER_FIELDS = [
    { key: 'ddd_id', label: 'DDD ID', required: true },
    { key: 'member_medicaid_id', label: 'Member Medicaid ID' },
    { key: 'provider_medicaid_id', label: 'Provider Medicaid ID' },
    { key: 'prior_authorization_number', label: 'Prior Authorization Number', required: true },
    { key: 'pa_start_date', label: 'PA Start Date' },
    { key: 'pa_end_date', label: 'PA End Date' },
    { key: 'approved_units_till_date', label: 'Approved Units Till Date' },
    { key: 'procedure_code', label: 'Procedure Code', required: true },
    { key: 'distribution_standard', label: 'Distribution Standard' },
];

// Shown and saved as the provider Medicaid ID when the document has none, so a prior
// authorization is never inserted without one. A dummy value the user is told to change.
export const DEFAULT_PROVIDER_MEDICAID_ID = '12345';
const DEFAULT_PROVIDER_MEDICAID_NOTE = 'Not in the document. Placeholder value: replace it with your provider Medicaid ID if you have one, otherwise leave it as is.';

export function providerMedicaidOrDefault(value) {
    return String(value ?? '').trim() || DEFAULT_PROVIDER_MEDICAID_ID;
}

function parseRateFloat(val) {
    if (val == null) return NaN;
    return parseFloat(String(val).replace(/[$,\s]/g, ''));
}

function parseDateSafe(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
}

const PROC_PLACEHOLDERS = new Set(['', 'N/A', 'NA', 'NONE', 'NULL', 'UNDEFINED']);

function isPlaceholderProc(value) {
    return PROC_PLACEHOLDERS.has(String(value ?? '').trim().toUpperCase());
}

/**
 * Resolve a prior-auth entry's procedure code. When the PA header field is
 * blank, fall back to the plan's service authorizations — but only when they
 * resolve to a single distinct procedure code. An ambiguous multi-service plan
 * is left unresolved rather than guessed.
 */
export function resolveProcedureCode(rawProcCode, serviceAuthorizations) {
    if (!isPlaceholderProc(rawProcCode)) return String(rawProcCode).trim();
    const distinct = Array.from(new Set(
        (serviceAuthorizations || [])
            .map((s) => String(s?.procedure_code ?? '').trim())
            .filter((code) => !isPlaceholderProc(code))
    ));
    return distinct.length === 1 ? distinct[0] : '';
}

function calculateAuthorizedAmount(authorizedUnits, startDate, procedureCode, serviceAuthorizations, rateChanges) {
    if (authorizedUnits == null || authorizedUnits === '') return null;
    const units = parseFloat(String(authorizedUnits));
    if (isNaN(units)) return null;
    if (!procedureCode) return null;

    const svcAuth = (serviceAuthorizations || []).find(
        (s) => s && s.procedure_code === procedureCode
    );
    if (!svcAuth) return null;

    let rate = parseRateFloat(svcAuth.rate);
    if (isNaN(rate)) return null;

    const startDateObj = parseDateSafe(startDate);
    if (startDateObj && Array.isArray(rateChanges)) {
        const applicableChange = rateChanges
            .filter((c) => c && c.procedure_code === procedureCode)
            .find((c) => {
                const effectiveDate = parseDateSafe(c.new_rate_effective_date);
                return effectiveDate && startDateObj > effectiveDate;
            });
        if (applicableChange) {
            const newRate = parseRateFloat(applicableChange.new_rate);
            if (!isNaN(newRate)) rate = newRate;
        }
    }

    return `$${(units * rate).toFixed(2)}`;
}

function initLocalData(data, derivedDddId, derivedMemberMedicaidId, serviceAuthorizations) {
    if (!Array.isArray(data)) return [];
    return data.map((p) => ({
        ...p,
        ddd_id: derivedDddId || p.ddd_id || '',
        member_medicaid_id: derivedMemberMedicaidId || p.member_medicaid_id || '',
        procedure_code: resolveProcedureCode(p.procedure_code, serviceAuthorizations),
    }));
}

// Distribution columns, keyed by the distribution row field each one shows.
const DISTRIBUTION_COLUMNS = [
    { key: 'row_number', label: 'Row #' },
    { key: 'start_date', label: 'Start Date' },
    { key: 'end_date', label: 'End Date' },
    { key: 'authorized_units', label: 'Authorized Units' },
];

// `saveStatusOf(section, path)` says whether Proceed saves the field at a path of the prior authorizations.
const PriorAuthTable = ({ data, serviceAuthorizations, rateChanges, customerInformation, onSave, saveStatusOf }) => {
    const derivedDddId = useMemo(
        () => String(
            customerInformation?.ddd_id
            || customerInformation?.customer_id
            || customerInformation?.DDDID
            || ''
        ).trim(),
        [customerInformation]
    );
    const derivedMemberMedicaidId = useMemo(
        () => String(customerInformation?.medicaid_id || '').trim(),
        [customerInformation]
    );
    // Stable string of the service-auth-derived procedure code so the re-sync
    // effect below doesn't loop on the serviceAuthorizations array's identity.
    const fallbackProcedureCode = useMemo(
        () => resolveProcedureCode('', serviceAuthorizations),
        [serviceAuthorizations]
    );

    const [localData, setLocalData] = useState(() => initLocalData(data, derivedDddId, derivedMemberMedicaidId, serviceAuthorizations));

    // Re-sync the customer-derived IDs and procedure-code fallback onto every
    // entry whenever the customer info or service authorizations change.
    useEffect(() => {
        setLocalData((prev) => prev.map((p) => ({
            ...p,
            ddd_id: derivedDddId || p.ddd_id || '',
            member_medicaid_id: derivedMemberMedicaidId || p.member_medicaid_id || '',
            procedure_code: isPlaceholderProc(p.procedure_code) ? fallbackProcedureCode : String(p.procedure_code).trim(),
        })));
    }, [derivedDddId, derivedMemberMedicaidId, fallbackProcedureCode]);

    // Header-level editing
    const [headerEditIdx, setHeaderEditIdx] = useState(null);
    const [headerEditData, setHeaderEditData] = useState({});

    // Distribution row editing
    const [editRowId, setEditRowId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // ── Header edit handlers ──────────────────────────────────────────────────

    const handleHeaderEdit = (pIdx) => {
        setHeaderEditIdx(pIdx);
        setHeaderEditData({ ...localData[pIdx], provider_medicaid_id: providerMedicaidOrDefault(localData[pIdx]?.provider_medicaid_id) });
    };

    const handleHeaderSave = (pIdx) => {
        const updated = localData.map((p, i) =>
            i === pIdx
                ? {
                    ...headerEditData,
                    ddd_id: derivedDddId || headerEditData.ddd_id || '',
                    member_medicaid_id: derivedMemberMedicaidId || headerEditData.member_medicaid_id || '',
                }
                : p
        );
        setLocalData(updated);
        setHeaderEditIdx(null);
        setHeaderEditData({});
        onSave(updated);
    };

    const handleHeaderCancel = () => {
        setHeaderEditIdx(null);
        setHeaderEditData({});
    };

    const handleHeaderFieldChange = (key, value) => {
        setHeaderEditData((prev) => ({ ...prev, [key]: value }));
    };

    // ── Row edit handlers ─────────────────────────────────────────────────────

    const handleRowEdit = (pIdx, dIdx) => {
        setEditRowId(`${pIdx}-${dIdx}`);
        setEditFormData({ ...localData[pIdx].distribution[dIdx] });
    };

    const handleRowSave = (pIdx, dIdx) => {
        const updated = localData.map((p, i) => {
            if (i !== pIdx) return p;
            const newDist = (p.distribution || []).map((d, j) =>
                j === dIdx ? { ...editFormData } : d
            );
            return { ...p, distribution: newDist };
        });
        setLocalData(updated);
        setEditRowId(null);
        setEditFormData({});
        onSave(updated);
    };

    const handleRowCancel = () => {
        setEditRowId(null);
        setEditFormData({});
    };

    const handleRowFieldChange = (key, value) => {
        setEditFormData((prev) => ({ ...prev, [key]: value }));
    };

    // ─────────────────────────────────────────────────────────────────────────

    if (!localData || localData.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No prior authorization data available.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {localData.map((P, pIdx) => {
                const isEditingHeader = headerEditIdx === pIdx;

                return (
                    <Box
                        key={pIdx}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                        }}
                    >
                        {/* ── Block title + header edit toggle ─────────────── */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 2,
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600}>
                                Prior Authorization {pIdx + 1}
                            </Typography>

                            {!isEditingHeader ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleHeaderEdit(pIdx)}
                                >
                                    Edit Header
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() => handleHeaderSave(pIdx)}
                                    >
                                        Save Header
                                    </Button>
                                    <Button size="small" onClick={handleHeaderCancel}>
                                        Cancel
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {/* ── Header fields grid ───────────────────────────── */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: '1fr 1fr 1fr',
                                },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            {PRIOR_AUTH_HEADER_FIELDS.map(({ key, label, required }) => {
                                const isEditable =
                                    key === 'prior_authorization_number' || key === 'provider_medicaid_id';
                                if (isEditingHeader && isEditable) {
                                    const value = headerEditData[key] ?? '';
                                    const showError = required && !String(value).trim();
                                    const isDefaultProvider = key === 'provider_medicaid_id' && String(value).trim() === DEFAULT_PROVIDER_MEDICAID_ID;
                                    return (
                                        <TextField
                                            key={key}
                                            size="small"
                                            fullWidth
                                            required={required}
                                            error={showError}
                                            helperText={showError ? 'Required' : (isDefaultProvider ? DEFAULT_PROVIDER_MEDICAID_NOTE : undefined)}
                                            label={label}
                                            value={value}
                                            onChange={(e) =>
                                                handleHeaderFieldChange(key, e.target.value)
                                            }
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    );
                                }

                                const saveStatus = saveStatusOf ? saveStatusOf(localData, [pIdx, key]) : null;
                                const hasValue =
                                    P[key] !== undefined && P[key] !== null && P[key] !== '';
                                const isMissingRequired = required && !hasValue;
                                const isProvider = key === 'provider_medicaid_id';
                                const displayVal = isProvider
                                    ? providerMedicaidOrDefault(P[key])
                                    : hasValue
                                        ? String(P[key])
                                        : (isMissingRequired ? 'Required' : '—');
                                const isDefaultProvider = isProvider && displayVal === DEFAULT_PROVIDER_MEDICAID_ID;
                                return (
                                    <Box key={key}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mb: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <span>
                                                {label}
                                                {required && (
                                                    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                )}
                                            </span>
                                            {saveStatus && <SaveStatusIcon saved={saveStatus.saved} note={saveStatus.note} />}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={isMissingRequired ? 'error.main' : (saveStatus && !saveStatus.saved ? 'text.secondary' : 'text.primary')}
                                            fontWeight={isMissingRequired ? 500 : 400}
                                        >
                                            {displayVal}
                                        </Typography>
                                        {isDefaultProvider && (
                                            <Typography variant="caption" color="warning.main">
                                                {DEFAULT_PROVIDER_MEDICAID_NOTE}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* ── Distribution table ───────────────────────────── */}
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                            Distribution
                        </Typography>

                        {(!P.distribution || P.distribution.length === 0) ? (
                            <Typography variant="body2" color="text.secondary">
                                No distribution entries.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {DISTRIBUTION_COLUMNS.map(({ key, label }) => {
                                                const saveStatus = saveStatusOf ? saveStatusOf(localData, [pIdx, 'distribution', 0, key]) : null;
                                                return (
                                                    <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>
                                                        {label}
                                                        {saveStatus && (
                                                            <Box component="span" sx={{ ml: 0.5 }}>
                                                                <SaveStatusIcon saved={saveStatus.saved} note={saveStatus.note} />
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                Authorized Amount
                                                {saveStatusOf && (
                                                    <Box component="span" sx={{ ml: 0.5 }}>
                                                        <SaveStatusIcon saved note="Worked out from the service's rate." />
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {P.distribution.map((D, dIdx) => {
                                            const rowId = `${pIdx}-${dIdx}`;
                                            const isEditing = editRowId === rowId;

                                            const previewUnits = isEditing
                                                ? editFormData.authorized_units
                                                : D.authorized_units;
                                            const previewStartDate = isEditing
                                                ? editFormData.start_date
                                                : D.start_date;

                                            const authorizedAmount = calculateAuthorizedAmount(
                                                previewUnits,
                                                previewStartDate,
                                                P.procedure_code,
                                                serviceAuthorizations,
                                                rateChanges
                                            );

                                            return (
                                                <TableRow key={dIdx}>
                                                    {/* Row number */}
                                                    <TableCell>
                                                        {D.row_number != null ? D.row_number : dIdx + 1}
                                                    </TableCell>

                                                    {/* Start Date */}
                                                    <TableCell>
                                                        {isEditing ? (
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.start_date ?? ''}
                                                                onChange={(e) =>
                                                                    handleRowFieldChange('start_date', e.target.value)
                                                                }
                                                                sx={{ width: 140 }}
                                                            />
                                                        ) : (
                                                            D.start_date ?? '—'
                                                        )}
                                                    </TableCell>

                                                    {/* End Date */}
                                                    <TableCell>
                                                        {isEditing ? (
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.end_date ?? ''}
                                                                onChange={(e) =>
                                                                    handleRowFieldChange('end_date', e.target.value)
                                                                }
                                                                sx={{ width: 140 }}
                                                            />
                                                        ) : (
                                                            D.end_date ?? '—'
                                                        )}
                                                    </TableCell>

                                                    {/* Authorized Units */}
                                                    <TableCell>
                                                        {isEditing ? (
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={editFormData.authorized_units ?? ''}
                                                                onChange={(e) =>
                                                                    handleRowFieldChange(
                                                                        'authorized_units',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                sx={{ width: 100 }}
                                                                slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                                                            />
                                                        ) : (
                                                            D.authorized_units ?? '—'
                                                        )}
                                                    </TableCell>

                                                    {/* Authorized Amount (derived, read-only) */}
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            color={
                                                                authorizedAmount
                                                                    ? 'success.main'
                                                                    : 'text.disabled'
                                                            }
                                                            fontWeight={authorizedAmount ? 500 : 400}
                                                        >
                                                            {authorizedAmount ?? '—'}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Actions */}
                                                    <TableCell>
                                                        {isEditing ? (
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => handleRowSave(pIdx, dIdx)}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    onClick={handleRowCancel}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => handleRowEdit(pIdx, dIdx)}
                                                                disabled={editRowId !== null && editRowId !== rowId}
                                                            >
                                                                Edit
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default PriorAuthTable;
